// src/pages/EventsPage.js

import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import { eventService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';

// Styled components
const EventsSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const EventsHeader = styled.div`
  margin-bottom: 3rem;
`;

const EventsFilter = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterButton = styled(motion.button)`
  padding: 0.5rem 1.5rem;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.text + '50'};
  border-radius: 30px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.backgroundSecondary};
  }
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const EventCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  
  &:hover {
    box-shadow: ${props => props.theme.card.hover};
  }
`;

const EventImage = styled.div`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.src || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const EventStatus = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  background-color: ${props => {
    switch (props.status) {
      case 'upcoming':
        return props.theme.accent;
      case 'ongoing':
        return props.theme.primary;
      case 'completed':
        return props.theme.success;
      case 'cancelled':
        return props.theme.error;
      default:
        return props.theme.text;
    }
  }};
`;

const EventContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const EventTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => props.theme.primary};
    }
  }
`;

const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.text};
  
  div {
    display: flex;
    align-items: flex-start;
    
    i {
      width: 20px;
      margin-right: 0.5rem;
      margin-top: 0.2rem;
      color: ${props => props.theme.primary};
    }
  }
`;

const EventDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const EventFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const ParticipantsCount = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  
  i {
    margin-right: 0.5rem;
  }
`;

const EventButton = styled.a`
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: ${props => props.primary ? props.theme.primary : 'transparent'};
  color: ${props => props.primary ? 'white' : props.theme.primary};
  border: 1px solid ${props => props.theme.primary};
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.primary ? props.theme.primaryDark || props.theme.primary + 'dd' : props.theme.primary + '10'};
    transform: translateY(-2px);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
`;

const EventsPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Référence pour l'animation avec IntersectionObserver
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  // Charger les événements depuis l'API
  React.useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        
        // Paramètres de l'API pour le filtrage
        let params = {};
        if (activeFilter !== 'all') {
          params.status = activeFilter;
        }
        
        const response = await eventService.getAll(params);
        
        // Vérifier la structure de la réponse
        let eventsData = [];
        if (response.data && Array.isArray(response.data.results)) {
          eventsData = response.data.results;
        } else if (Array.isArray(response.data)) {
          eventsData = response.data;
        }
        
        console.log("Events data:", eventsData);
        setEvents(eventsData);
        setError(null);
        
        // Mise à jour du titre de la page
        document.title = `${t('ourEvents')} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err.response?.data?.detail || t('errorLoadingEvents'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [activeFilter, t]);
  
  // Gérer le changement de filtre
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Traduction du statut de l'événement
  const getStatusLabel = (status) => {
    switch (status) {
      case 'upcoming':
        return t('upcoming');
      case 'ongoing':
        return t('ongoing');
      case 'completed':
        return t('completed');
      case 'cancelled':
        return t('cancelled');
      default:
        return status;
    }
  };
  
  // Obtenir un extrait de la description
  const getDescriptionExcerpt = (description) => {
    if (!description) return '';
    
    // Supprimer les balises HTML si présentes
    const plainText = description.replace(/<[^>]*>?/gm, '');
    
    return plainText.length > 150 ? plainText.substring(0, 150) + '...' : plainText;
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay error={error} />;
  }
  
  return (
    <EventsSection>
      <Container>
        <EventsHeader>
          <SectionTitle
            title={t('ourEvents')}
            subtitle={t('ourEventsSubtitle')}
          />
        </EventsHeader>
        
        <EventsFilter>
          <FilterButton
            theme={theme}
            active={activeFilter === 'all'}
            onClick={() => handleFilterChange('all')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('allEvents')}
          </FilterButton>
          
          <FilterButton
            theme={theme}
            active={activeFilter === 'upcoming'}
            onClick={() => handleFilterChange('upcoming')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('upcoming')}
          </FilterButton>
          
          <FilterButton
            theme={theme}
            active={activeFilter === 'ongoing'}
            onClick={() => handleFilterChange('ongoing')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('ongoing')}
          </FilterButton>
          
          <FilterButton
            theme={theme}
            active={activeFilter === 'completed'}
            onClick={() => handleFilterChange('completed')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('completed')}
          </FilterButton>
        </EventsFilter>
        
        {events.length === 0 ? (
          <NoResults theme={theme}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                <i className="fas fa-calendar-times"></i>
              </div>
              <h3>{t('noEventsFound')}</h3>
              <p>{t('checkBackLater')}</p>
            </motion.div>
          </NoResults>
        ) : (
          <EventsGrid ref={ref}>
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <EventCard theme={theme}>
                  <EventImage src={event.image}>
                    <EventStatus theme={theme} status={event.status}>
                      {getStatusLabel(event.status)}
                    </EventStatus>
                  </EventImage>
                  
                  <EventContent>
                    <EventTitle theme={theme}>
                      <a href={`/events/${event.slug || event.id}`}>{event.title}</a>
                    </EventTitle>
                    
                    <EventMeta theme={theme}>
                      <div>
                        <i className="far fa-calendar-alt"></i>
                        <span>
                          {formatDate(event.date || event.start_date)}
                          {(event.end_date && event.end_date !== (event.date || event.start_date)) && 
                            ` - ${formatDate(event.end_date)}`}
                        </span>
                      </div>
                      <div>
                        <i className="far fa-clock"></i>
                        <span>{event.time || '00:00'} {event.end_time && `- ${event.end_time}`}</span>
                      </div>
                      <div>
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{event.is_online ? t('onlineEvent') : (event.location || t('locationToBeDetermined'))}</span>
                      </div>
                    </EventMeta>
                    
                    <EventDescription theme={theme}>
                      {getDescriptionExcerpt(event.description)}
                    </EventDescription>
                    
                    <EventFooter>
                      <ParticipantsCount theme={theme}>
                        <i className="fas fa-users"></i>
                        {event.current_participants || event.participants?.length || 0} / 
                        {event.max_participants ? event.max_participants : '∞'}
                      </ParticipantsCount>
                      
                      <EventButton 
                        href={`/events/${event.id}`}
                        theme={theme}
                        primary={event.status === 'upcoming'}
                      >
                        {event.status === 'upcoming' ? t('registerNow') : t('viewDetails')}
                      </EventButton>
                    </EventFooter>
                  </EventContent>
                </EventCard>
              </motion.div>
            ))}
          </EventsGrid>
        )}
      </Container>
    </EventsSection>
  );
};

export default EventsPage;